import { ChangeDetectionStrategy, Component, Input, NgZone, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { MapService } from '../map.service';
import { PERMISSIONS, PermissionService } from '../../../../core/services/permission.service';
import { RouterHelperService } from '../../../../core/services/router-helper.service';
import { Zone } from '../../../../interfaces/zone';

export const ZONE_EDIT_QUERY_PARAM = 'zoneEdit';
export const ZONE_BOUNDS_QUERY_PARAM = 'zoneBounds';

@Component({
  selector: 'app-map-zone-contextmenu',
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MapZoneContextmenuComponent implements OnInit, OnDestroy {
  @Input() disabled = false;
  private subscription: Subscription = new Subscription();
  private mapInstance: H.Map;

  constructor(private mapService: MapService,
              private permissionService: PermissionService,
              private translateService: TranslateService,
              private routerHelper: RouterHelperService,
              private ngZone: NgZone) {
  }

  ngOnInit(): void {
    this.subscription.add(this.mapService.mapIsLoad.subscribe((mapInstance: H.Map) => {
      this.removeListener();
      this.mapInstance = mapInstance;
      this.mapInstance.addEventListener('contextmenu', this.handlerContextMenuEvent);
    }));
  }

  handlerContextMenuEvent = (e: any) => {
    if (this.disabled || !this.permissionService.hasPermission(PERMISSIONS.ZONE_UPDATE)) {
      return;
    }
    const zones: Zone[] = this.zonesAt(e.viewportX, e.viewportY);
    if (!zones.length) {
      return;
    }
    const items: H.util.ContextItem[] = zones.map((zone: Zone) => new H.util.ContextItem({
      label: this.translateService.instant('EDIT_ZONE_NAME', {name: zone.name}),
      callback: () => this.ngZone.run(() => this.editZone(zone)),
    }));
    if (e.items.length) {
      items.push(H.util.ContextItem.SEPARATOR);
    }
    e.items.unshift(...items);
  }

  zonesAt(x: number, y: number): Zone[] {
    const point: H.geo.Point = this.mapInstance.screenToGeo(x, y);
    if (!point) {
      return [];
    }
    const zones: Zone[] = [];
    this.mapInstance.getObjects()
      .filter((object: H.map.Object) => object instanceof H.map.Polygon)
      .filter((polygon: H.map.Polygon) => {
        const zone: Zone = polygon.getData();
        return zone && zone.id && this.polygonContainsPoint(polygon, point);
      })
      .forEach((polygon: H.map.Polygon) => {
        const zone: Zone = polygon.getData();
        if (!zones.some((z: Zone) => z.id === zone.id)) {
          zones.push(zone);
        }
      });
    return zones;
  }

  polygonContainsPoint(polygon: H.map.Polygon, point: H.geo.Point): boolean {
    if (!polygon.getBoundingBox().containsPoint(point)) {
      return false;
    }
    const geometry: any = polygon.getGeometry();
    const polygons: H.geo.Polygon[] = geometry.getPolygons ? geometry.getPolygons() : [geometry];
    return polygons.some((geoPolygon: H.geo.Polygon) =>
      this.lineStringContainsPoint(geoPolygon.getExterior().getLatLngAltArray(), point));
  }

  // ray casting on flat [lat, lng, alt, lat, lng, alt, ...] array
  lineStringContainsPoint(coords: number[], point: H.geo.Point): boolean {
    let inside: boolean = false;
    const count: number = coords.length / 3;
    for (let i = 0, j = count - 1; i < count; j = i++) {
      const latI: number = coords[i * 3];
      const lngI: number = coords[i * 3 + 1];
      const latJ: number = coords[j * 3];
      const lngJ: number = coords[j * 3 + 1];
      if ((latI > point.lat) !== (latJ > point.lat)
        && point.lng < (lngJ - lngI) * (point.lat - latI) / (latJ - latI) + lngI) {
        inside = !inside;
      }
    }
    return inside;
  }

  editZone(zone: Zone) {
    const rect: H.geo.Rect = this.mapInstance.getViewModel().getLookAtData().bounds.getBoundingBox();
    const bounds: string = [rect.getTop(), rect.getLeft(), rect.getBottom(), rect.getRight()]
      .map((value: number) => value.toFixed(6))
      .join(',');
    this.routerHelper.navigatePopup(['zone', zone.id], {
      queryParams: {
        [ZONE_EDIT_QUERY_PARAM]: true,
        [ZONE_BOUNDS_QUERY_PARAM]: bounds,
      },
      queryParamsHandling: 'merge',
    });
  }

  removeListener() {
    if (this.mapInstance) {
      this.mapInstance.removeEventListener('contextmenu', this.handlerContextMenuEvent);
    }
  }

  ngOnDestroy(): void {
    this.removeListener();
    this.subscription.unsubscribe();
  }
}
